<template>
  <header class="order-header">
    <div class="order-header__top">
      <div class="order-header__identity">
        <h1 class="order-header__title">Order {{ order.id }}</h1>
        <button
          v-if="customerName && customerLinkable"
          type="button"
          class="order-header__customer order-header__customer--link"
          data-testid="admin-order-customer"
          @click="$emit('view-customer', { student_id: order.student.id })"
        >
          {{ customerName }}
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
        <p
          v-else-if="customerName"
          class="order-header__customer"
          data-testid="admin-order-customer"
        >
          {{ customerName }}
        </p>
      </div>
      <p class="order-header__status" data-testid="admin-order-status">
        <span
          class="order-header__pill"
          :class="'order-header__pill--' + status.tone"
          >{{ status.label }}</span
        >
        <span v-if="status.showsBalance" class="order-header__due"
          >{{ formatAmount(amountOwed) }} due</span
        >
      </p>
    </div>
    <dl class="order-header__meta">
      <div class="order-header__pair">
        <dt>Purchased</dt>
        <dd>{{ purchasedOn }}</dd>
      </div>
      <div v-if="source" class="order-header__pair">
        <dt>Source</dt>
        <dd>{{ source }}</dd>
      </div>
      <div v-if="createdBy" class="order-header__pair">
        <dt>Created by</dt>
        <dd>{{ createdBy }}</dd>
      </div>
    </dl>
  </header>
</template>

<script>
import { formatDateTime } from "../../helpers/time";
import { inDollars } from "../../../../DriveScout/Billing/Teller";
import { sourceLabel } from "./orderView";

const DATE_TIME_FORMAT = "MM/DD/YYYY h:mm a";

function fullName(person) {
  if (!person || typeof person !== "object") {
    return "";
  }
  return `${person.firstname} ${person.lastname}`;
}

export default {
  name: "OrderHeader",
  props: {
    order: { type: Object, required: true },
    // orderStatus() from orderView.ts
    status: { type: Object, required: true },
    amountOwed: { type: Number, default: 0 },
    customerLinkable: { type: Boolean, default: false },
  },
  computed: {
    // An order whose student was deleted arrives with student: null.
    customerName() {
      return fullName(this.order.student);
    },
    purchasedOn() {
      return formatDateTime(this.order.created_at, DATE_TIME_FORMAT);
    },
    source() {
      return sourceLabel(this.order.source);
    },
    createdBy() {
      return fullName(this.order.created_by);
    },
  },
  methods: {
    formatAmount(amount) {
      return inDollars(amount);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/css/partials/variables.scss";

.order-header {
  color: $grey;
}

.order-header__top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px 24px;
}

.order-header__identity {
  min-width: 0;
}

.order-header__title {
  margin: 0;
  font-size: 24px;
  line-height: 1.25;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.order-header__customer {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-height: 44px;
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 600;
}

.order-header__customer--link {
  border: 0;
  background: none;
  color: $green-ink;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $green;
    outline-offset: 2px;
  }
}

.order-header__status {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;

  @media #{$sevensixeight} {
    width: auto;
    margin-top: 6px;
  }
}

.order-header__pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;

  // The dot repeats the tone; the label carries the meaning, so the dot is decoration.
  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
}

.order-header__pill--green {
  background: mix($green, $white, 18%);

  &::before {
    background: $green-ink;
  }
}

.order-header__pill--yellow {
  background: mix($yellow, $white, 25%);

  &::before {
    background: $dark-yellow;
  }
}

.order-header__pill--red {
  background: mix($red, $white, 15%);

  &::before {
    background: $danger-ink;
  }
}

.order-header__pill--grey {
  background: $light;

  &::before {
    background: $blue-grey;
  }
}

.order-header__due {
  font-size: 15px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.order-header__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 12px 0 0;

  @media #{$sevensixeight} {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px 40px;
  }

  dt {
    font-size: 13px;
    font-weight: normal;
    color: $blue-grey;
  }

  dd {
    margin: 0;
  }
}

.order-header__pair {
  display: flex;
  align-items: baseline;
  gap: 12px;

  dt {
    flex: none;
    width: 88px;
  }

  @media #{$sevensixeight} {
    display: block;

    dt {
      width: auto;
      margin-bottom: 2px;
    }
  }
}
</style>
